/**
 * 輕量 Modal 元件（REQ-20260418015）。
 * - 無 focus trap、無 portal（為現階段夠用；若使用者後續回報 a11y 問題再升級）
 * - ESC 關閉
 * - 點擊 backdrop 關閉（可關掉）
 * - 阻止 body scroll
 */
import { useEffect } from 'react';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** 點擊背景是否關閉；預設 true。表單型 modal 若有未送出資料，可設 false 避免誤關。 */
  dismissOnBackdrop?: boolean;
  /**
   * ESC 是否關閉;預設 true。**巢狀 Modal 時外層要設 false**(REQ-20260903009)——
   * ESC handler 掛在 `document` 上,兩層都註冊的話按一次會兩層一起關,
   * 使用者在確認視窗按 ESC 會連底下剛 key 完的表單一起消失。
   */
  dismissOnEsc?: boolean;
  widthClass?: string;
  /** Header 上 X 按鈕左側的插槽,用來顯示提示文字(例如「尚未儲存」)。 */
  headerNote?: React.ReactNode;
  children: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  dismissOnBackdrop = true,
  dismissOnEsc = true,
  widthClass = 'max-w-2xl',
  headerNote,
  children,
}: ModalProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    // 鎖 body scroll 與 ESC 無關,不論 dismissOnEsc 都要做
    if (dismissOnEsc) document.addEventListener('keydown', handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      if (dismissOnEsc) document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, dismissOnEsc]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 overflow-y-auto"
      onMouseDown={(e) => {
        if (dismissOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`bg-white rounded-lg shadow-xl w-full ${widthClass} my-10`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-lg font-semibold">{title}</h2>
          <div className="flex items-center gap-3">
            {headerNote}
            <button
              type="button"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-800 text-xl leading-none"
              aria-label="關閉"
            >
              ×
            </button>
          </div>
        </header>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
